<template>
    <ContentComp>
        <AlertMessage
            v-if="!raceContestantStartTime"
            type="warning"
        >
            {{ $t('timeedit.contestantnostarttime') }}
        </AlertMessage>
        <AlertMessage
            v-if="clockTimeIsEarlierThanStarttime()"
            type="danger"
        >
            {{ $t('timeedit.timeearlierthanstarttime') }}
        </AlertMessage>
        <FormLayout :labelWidth="160">
            <FormLayoutRow v-if="quickSelectTimes.length">
                <FormLayoutLabel text="general.selecttime" />
                <DropdownComp
                    class="flex-fill w-100"
                    :dropdown-items="quickSelectTimes"
                    translationkey="general.selecttime"
                />
            </FormLayoutRow>
            <FormLayoutRow>
                <FormLayoutLabel text="general.passingtime" />
                <InputNullableDateTime
                    :format="clockTimeFormat"
                    v-model="clockTime"
                    :autoFillOnBlur="true"
                    :prefillDate="usedTimeRelativeToDate"
                />
            </FormLayoutRow>
            <FormLayoutRow>
                <FormLayoutLabel />
                <InputBool
                    v-model="showDate"
                    label="general.showdate"
                />
            </FormLayoutRow>
            <FormLayoutRow v-if="raceContestantStartTime">
                <FormLayoutLabel text="general.usedtime" />
                <InputNullableDateTime
                    :format="usedTimeFormat"
                    v-model="usedTimeDate"
                    :autoFillOnBlur="true"
                />
            </FormLayoutRow>
        </FormLayout>
    </ContentComp>
</template>
<script setup lang="ts">
import { AlertMessage, DropdownComp, InputBool, InputNullableDateTime } from '@/components';
import { computed, ref } from 'vue';
import { AddTimeRequest, ContestantTime, type StationRole } from '@/objects/generated';
import {
    dateFormatHasDate,
    dateToMilliseconds,
    dateTypeToFullDateType,
    dateTypeToTimeOnlyType,
    getDefaultDateWithTime,
    millisecondsToJsDate,
    mergeDatePartAndTimePart,
    type DateType,
    formatDateToInputFormat,
} from '@/utilities/dateutils';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import { TimeDao } from '@/dao';
import { useEventCommonStore, useEventStore } from '@/stores';
import ContentComp from '@/components/layout/ContentComp.vue';
import { useTransactions } from '@/composeables';
import { DropdownItemButton } from '@/objects';

const props = defineProps<{
    /**
     * Id of the contestant we are editing the time for
     */
    contestantId?: number | null;

    /**
     * RacecontestantId of the contestant we are editing the time for
     */
    raceContestantId?: number | null;

    /**
     * The bib of the contestant we are editing the time for
     * If contestantId is not set, this will be used to add a new time for the contestant with this bib
     */
    bib?: number | null;

    /**
     * The stationRole of the cell we are editing
     */
    stationRole: StationRole;

    /**
     * The starttime of the raceContestant (used to calculate the used time)
     */
    raceContestantStartTime?: Date | null;

    /**
     * The current used time. This is essentially the value we are editing in this dialog, so this should have the current value
     */
    usedTime: number | null;

    /**
     * The TUID of the time we are editing if there is an existing time here
     */
    editingTuid: string | null;

    /**
     * Function to get the times of the contestant. This is used to fill the quick select dropdown
     */
    contestantTimeGetter: (contestantId: number) => ContestantTime[];

    /**
     * Map of backup stations
     */
    StationIdToBackupStationIdsMap: Map<number, number[]>;
}>();

// Should we show the datepart of the clocktime or not. If we dont have a starttime we have should show the date since we dont have a relative time.
const showDate = ref(!props.raceContestantStartTime);

const format: DateType = 'TIMEMS3';

// The internal representation of the clocktime of the passing.
// This has the correct date
const clockTimeInternal = ref(
    props.raceContestantStartTime && props.usedTime ? new Date(props.raceContestantStartTime.getTime() + (props.usedTime ?? 0)) : null,
);

// If there is no starttime on the contestant we still need some sort of Date work with
// So we use the event date if we dont have the starttime.
// This will be the date part of the clocktime if we are using a dateformat without time
const usedTimeRelativeToDate = props.raceContestantStartTime ? props.raceContestantStartTime : useEventCommonStore().Event!.Date;

/**
 * The times shown in the quick select dropdown
 * Note, this isnt really reactive, but it looks cleaner to put the code inside a computed
 */
const quickSelectTimes = computed(() => {
    if (!props.contestantId) {
        return [];
    }

    const contestantTimes = props.contestantTimeGetter(props.contestantId).filter((ct) => ct.ClockTime !== null && ct.StationID !== null);
    const redTimes = contestantTimes.filter((ct) => ct.StationID === props.stationRole.StationID);
    const backupTimes = contestantTimes.filter((ct) =>
        (props.StationIdToBackupStationIdsMap.get(props.stationRole.StationID) ?? []).includes(ct.StationID!),
    );

    return [
        ...backupTimes.map((ct) =>
            DropdownItemButton.create({
                translationkey: formatDateToInputFormat(ct.ClockTime, 'TIMEMS3') + ' (BACKUP)',
                action: () => {
                    clockTime.value = new Date(ct.ClockTime);
                },
            }),
        ),
        ...redTimes.map((ct) =>
            DropdownItemButton.create({
                translationkey: formatDateToInputFormat(ct.ClockTime, 'TIMEMS3') + (ct.FreeText.includes('GPIO') ? ' (GPIO)' : ' (TIME)'),
                action: () => {
                    clockTime.value = new Date(ct.ClockTime);
                },
            }),
        ),
    ];
});

/**
 * Checks if the passing time is earlier than the start time.
 */
const clockTimeIsEarlierThanStarttime = () => {
    // If we dont have a starttime we cant be earlier than it
    if (!props.raceContestantStartTime) {
        return false;
    }

    // If we dont have a time yet, the time cant be earlier than the starttime
    if (clockTimeInternal.value == null) {
        return false;
    }

    return clockTimeInternal.value.getTime() < props.raceContestantStartTime.getTime();
};

// Clocktime is calculated from the start time and the used time
const clockTime = computed({
    get() {
        if (clockTimeInternal.value == null) {
            return null;
        }

        // If we have a date in the format we can return the clocktime directly
        if (dateFormatHasDate(clockTimeFormat.value)) {
            return new Date(clockTimeInternal.value.getTime());
        }

        // If we dont have the date in the format, we want to return a defaultDateTimeForIncompleteFormats with the time part set (this is what the InputDateTime will work with internally)
        return getDefaultDateWithTime(clockTimeInternal.value);
    },
    set(newValue) {
        if (newValue === null) {
            clockTimeInternal.value = null;
            return;
        }

        // If there is no date in the format, the InputDateTime returns a time relative to the defaultDateTimeForIncompleteFormats in the dateutils
        // In that case we want to convert it to milliseconds and add it to the usedTimeRelativeToDate
        if (!dateFormatHasDate(clockTimeFormat.value)) {
            clockTimeInternal.value = mergeDatePartAndTimePart(clockTimeInternal.value ?? usedTimeRelativeToDate, newValue);
        } else {
            // If there is a date in the format then we just use the newValue directly.
            clockTimeInternal.value = new Date(newValue.getTime());
        }
    },
});

// Used time is represented as a date so we can edit it using the inputdatetime component
const usedTimeDate = computed({
    get() {
        if (clockTimeInternal.value == null) {
            return null;
        }

        let relativeMs = clockTimeInternal.value.getTime() - usedTimeRelativeToDate.getTime();
        if (relativeMs < 0) {
            return null;
        }
        // This is the time between the starttime and the passingtime, converted to a date object that the inputdatetime can work with
        return millisecondsToJsDate(relativeMs);
    },
    set(newValue) {
        if (newValue === null) {
            clockTimeInternal.value = null;
            return;
        }

        // This is the starttime + the timespan that is in the usedTime box
        let relativeMs = usedTimeRelativeToDate.getTime() + dateToMilliseconds(newValue);

        clockTimeInternal.value = new Date(relativeMs);
    },
});

// Clocktime format is dependent on the showDate bool
const clockTimeFormat = computed(() => {
    if (showDate.value) {
        return dateTypeToFullDateType(format);
    }

    return dateTypeToTimeOnlyType(format);
});

// Used time is always shown as time only. TODO: This should have its own format so we can go above 24 hours
const usedTimeFormat = dateTypeToTimeOnlyType(format);

let addTimeRequest: AddTimeRequest | null = null;

/**
 * Saves the time to the server
 * Disables any existing time if we are editing an existing time
 */
const save = async () => {
    let disablePromise = undefined;

    if (props.editingTuid !== null) {
        disablePromise = useTransactions().DisableTransactions([props.editingTuid]);
    }

    // TODO: We should have a factory function that has all the required fields as required so we have to fill everything when we create a new object
    // This will break the next time we add something to AddTimeRequest
    addTimeRequest = AddTimeRequest.FromJsValues({
        StationRoleID: props.stationRole.ID,
        StationID: props.stationRole.StationID,
        Time: clockTimeInternal.value!,
        ContestantID: props.contestantId ?? undefined,
        Bib: props.bib,
        RaceContestantID: props.raceContestantId ?? undefined,
    });

    // Actually save the data to the server
    const addTimePromise = TimeDao.AddTimes(useEventStore().eventID, [addTimeRequest]);

    await Promise.all([disablePromise, addTimePromise]);
};

// Disables the save button if the clocktime is null
const saveButtonEnabled = () => clockTimeInternal.value !== null;

defineExpose({
    save,
    saveButtonEnabled,
    getAddTimeRequest: () => addTimeRequest,
});
</script>
